import { DeleteOutlined, EyeInvisibleOutlined, FolderAddOutlined, OpenAIOutlined, TranslationOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useModal } from "@refinedev/antd";
import { MenuProps, Modal } from "antd";
import { Dropdown } from "antd/lib";
import { useTextRewrite } from "components/hooks/textRewrite";
import { useTranslate } from "components/hooks/useTranslate";
import { useMultiUpdateCategories } from "components/hooks/useMultiUpdateCategories";
import { MultiGenerateTextModal } from "components/modals/multiGenerateText";
import { MultiTranslateTextModal } from "components/modals/multiTranslateText";
import { MultiAddCategoryModal, MultiDeleteCategoryModal } from "components/modals/multiCategory";
import useDataGridStore from "stores/dataGridStore";

export const useMultiGridMenuItems = () => {
  const items: MenuProps["items"] = [
    {
      label: "Generera texter",
      key: 1,
      icon: <OpenAIOutlined />,
    },
    {
      label: "Översätt texter",
      key: 2,
      icon: <TranslationOutlined />,
    },
    {
      label: "Översätt specifikationer",
      key: 5,
      icon: <UnorderedListOutlined />,
    },
    {
      label: "Lägg till kategorier",
      key: 3,
      icon: <FolderAddOutlined />,
    },
    {
      label: "Ta bort kategorier",
      key: 4,
      icon: <DeleteOutlined />,
    },
    {
      label: "Rensa dolda kategorier",
      key: 6,
      icon: <EyeInvisibleOutlined />,
    }
  ];

  return { menuItems: items };
};

export const SelectedRowsActions = () => {
  const { rewriteText } = useTextRewrite();
  const { translateText, translateSpecifications } = useTranslate();
  const { multiAddCategories, multiDeleteCategories, clearHiddenCategories } = useMultiUpdateCategories();
  const { menuItems } = useMultiGridMenuItems();
  const { 
    show: showTranslateContextModal, 
    close: closeTranslateContextModal, 
    modalProps: translateContextModalProps 
  } = useModal();
  const { 
    show: showTranslateModal, 
    close: closeTranslateModal, 
    modalProps: translateModalProps 
  } = useModal();
  const {
    show: showTranslateSpecificationsModal,
    close: closeTranslateSpecificationsModal,
    modalProps: translateSpecificationsModalProps,
  } = useModal();
  const { 
    show: showAddCategoryModal, 
    close: closeAddCategoryModal, 
    modalProps: addCategoryModalProps 
  } = useModal();
  const { 
    show: showDeleteCategoryModal, 
    close: closeDeleteCategoryModal, 
    modalProps: deleteCategoryModalProps 
  } = useModal();
  const { checkboxColumnActive, selectedRows, setSelectedRows, setShowCategoryRefreshBanner } = useDataGridStore()
  const selectedRowsCount = selectedRows ? Object.keys(selectedRows)?.length : 0;

  const onMultiButtonClick = (e: any) => {
    if (e.key == 1) {
      showTranslateContextModal();
    } else if (e.key == 2) {
      showTranslateModal();
    } else if (e.key == 5) {
      showTranslateSpecificationsModal();
    } else if (e.key == 3) {
      showAddCategoryModal();
    } else if (e.key == 4) {
      showDeleteCategoryModal();
    } else if (e.key == 6) {
      const productIds = Object.keys(selectedRows ?? {});
      Modal.confirm({
        title: "Rensa dolda kategorier",
        content: `Tar bort alla dolda kategorier på ${productIds.length} valda produkt(er). Synliga kategorier behålls, även om de är överkategori till en dold kategori.`,
        okText: "Rensa",
        cancelText: "Avbryt",
        okButtonProps: { danger: true },
        onOk: () => {
          clearHiddenCategories(productIds, () => setShowCategoryRefreshBanner(true));
          setSelectedRows({});
        },
      });
    }
  };

  const onTextRewriteFinish = async (data: any) => {
    closeTranslateContextModal();
    rewriteText(Object.keys(selectedRows ?? {}), data?.context);
    setSelectedRows({});
  }

  const onTranslateFinish = async (data: any) => {
    closeTranslateModal();
    translateText(Object.keys(selectedRows ?? {}), data.targetLanguages);
    setSelectedRows({});
  }

  const onTranslateSpecificationsFinish = async (data: any) => {
    closeTranslateSpecificationsModal();
    translateSpecifications(Object.keys(selectedRows ?? {}), data.targetLanguages);
    setSelectedRows({});
  }

  const onAddCategoryFinish = async (data: any) => {
    closeAddCategoryModal();
    multiAddCategories(Object.keys(selectedRows ?? {}), data.categoryIds);
    setSelectedRows({});
    setShowCategoryRefreshBanner(true);
  }

  const onDeleteCategoryFinish = async (data: any) => {
    closeDeleteCategoryModal();
    multiDeleteCategories(Object.keys(selectedRows ?? {}), data.categoryIds);
    setSelectedRows({});
    setShowCategoryRefreshBanner(true);
  }

  return (
    <>
      <Dropdown.Button
        menu={{ items: menuItems, onClick: onMultiButtonClick }}
        disabled={!checkboxColumnActive || selectedRowsCount === 0}
        onClick={() => showTranslateContextModal()}
      >
        Generera texter
      </Dropdown.Button>
      <MultiGenerateTextModal
        modalProps={translateContextModalProps}
        onFinish={onTextRewriteFinish}
      />
      <MultiTranslateTextModal
        modalProps={translateModalProps}
        onFinish={onTranslateFinish}
      />
      <MultiTranslateTextModal
        modalProps={translateSpecificationsModalProps}
        onFinish={onTranslateSpecificationsFinish}
        title="Välj språk för specifikationsöversättning"
        description="Valda produkters specifikationsvärden (från svenska) kommer att översättas till det valda språket. Befintliga översättningar kommer att skrivas över."
      />
      <MultiAddCategoryModal
        modalProps={addCategoryModalProps}
        onFinish={onAddCategoryFinish}
        selectedIds={Object.keys(selectedRows ?? {})}
      />
      <MultiDeleteCategoryModal
        modalProps={deleteCategoryModalProps}
        onFinish={onDeleteCategoryFinish}
        selectedIds={Object.keys(selectedRows ?? {})}
      />
    </>
  );
};
